import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { boardsAPI, authAPI, platformAPI, onboardingAPI } from '../api';
import CreateBoardModal from '../components/CreateBoardModal';
import WelcomeCard from '../components/WelcomeCard';
import StorageWarningBanner from '../components/StorageWarningBanner';
import LegalFooterLinks from '../components/LegalFooterLinks';
import PlatformFooter from '../components/PlatformFooter';
import ArchiveModeBanner from '../components/ArchiveModeBanner';
import { getTranslation } from '../i18n';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  document.title = 'Maestra - Workspace';

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [workspaceLang, setWorkspaceLang] = useState('en');
  const [userRole, setUserRole] = useState(Cookies.get('kahla_role') || 'user');
  const [branding, setBranding] = useState(null);
  const [ptx, setPtx] = useState(null);
  const [accessStatus, setAccessStatus] = useState('active');
  const [archiveMode, setArchiveMode] = useState(null);
  const [isSupportView, setIsSupportView] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  // Minimal status filter: 'active' | 'expired' | 'all'
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    // Handle admin support token
    const supportToken = searchParams.get('support_token');
    if (supportToken) {
      Cookies.set('kahla_token', supportToken, { expires: 1/24, path: '/', sameSite: 'Lax', secure: window.location.protocol === 'https:' });
      Cookies.set('kahla_role', 'user', { expires: 1/24, path: '/', sameSite: 'Lax', secure: window.location.protocol === 'https:' });
      setIsSupportView(true);
      setUserRole('user');
      // Remove query param
      window.history.replaceState({}, '', '/dashboard');
    }
    fetchBoards();
    fetchWorkspaceSettings();
    platformAPI.getTexts().then(res => setPtx(res.data)).catch(() => {});
    onboardingAPI.state().then(res => setOnboarding(res.data)).catch(() => {});
  }, []);

  const fetchWorkspaceSettings = async () => {
    try {
      const { data } = await authAPI.getSettings();
      setWorkspaceLang(data.language || 'en');
      if (data.role) setUserRole(data.role);
      if (data.branding) setBranding(data.branding);
      if (data.access_status) setAccessStatus(data.access_status);
      if (data.archive_mode) setArchiveMode(data.archive_mode);
    } catch (err) {
      // Default to 'en'
    }
  };


  const t = getTranslation(workspaceLang);

  const fetchBoards = async () => {
    try {
      const { data } = await boardsAPI.list();
      setBoards(data);
    } catch (err) {
      console.error('Error fetching boards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    Cookies.remove('kahla_token');
    Cookies.remove('kahla_role');
    navigate('/login');
  };

  const handleCreateBoard = async (boardData) => {
    try {
      await boardsAPI.create(boardData);
      setShowCreateModal(false);
      fetchBoards();
      showToast('Board created successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'board_limit_reached') {
        showToast("You've reached your current board limit.");
        setShowCreateModal(false);
      } else {
        console.error('Error creating board:', err);
      }
    }
  };

  const handleDuplicate = async (e, boardId) => {
    e.stopPropagation();
    try {
      await boardsAPI.duplicate(boardId);
      fetchBoards();
      showToast('Board duplicated successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'board_limit_reached') {
        showToast("You've reached your current board limit.");
      } else {
        console.error('Error duplicating board:', err);
      }
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'OPEN': return 'status-badge status-open';
      case 'LOCKED': return 'status-badge status-locked';
      case 'EXPIRED':
      case 'ARCHIVED': return 'status-badge status-expired';
      default: return 'status-badge';
    }
  };

  // Inline overrides for each status — elegant, premium, immediately
  // distinguishable. Sage-tinted ivory for OPEN, warm parchment for LOCKED,
  // muted dusk-grey with darker text for EXPIRED.
  const getStatusBadgeStyle = (status) => {
    const base = {
      position: 'absolute',
      top: '14px',
      right: '14px',
      padding: '5px 11px',
      fontSize: '9.5px',
      letterSpacing: '0.16em',
      borderRadius: '999px',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      fontWeight: 500,
      textTransform: 'uppercase',
      lineHeight: 1.2,
    };
    switch (status) {
      case 'OPEN':
        return {
          ...base,
          background: 'rgba(245, 248, 240, 0.92)',
          color: '#4F6B47',
          border: '1px solid rgba(122, 155, 114, 0.28)',
          boxShadow: '0 1px 3px rgba(122,155,114,0.08)',
        };
      case 'LOCKED':
        return {
          ...base,
          background: 'rgba(252, 246, 235, 0.92)',
          color: '#8B5A00',
          border: '1px solid rgba(139, 90, 0, 0.18)',
          boxShadow: '0 1px 3px rgba(139,90,0,0.06)',
        };
      case 'EXPIRED':
      case 'ARCHIVED':
        return {
          ...base,
          background: 'rgba(60, 50, 45, 0.78)',
          color: 'rgba(255,253,250,0.92)',
          border: '1px solid rgba(60,50,45,0.4)',
          boxShadow: '0 1px 4px rgba(60,50,45,0.18)',
        };
      default:
        return { ...base, background: 'rgba(255,255,255,0.85)', color: 'var(--kahla-charcoal)' };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="dashboard fade-in">
      <header className="dashboard-header">
        <div>
          <button
            type="button"
            data-testid="boards-back-workspace"
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'transparent', border: 'none', padding: 0, marginBottom: 18, cursor: 'pointer',
              fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: 'var(--kahla-text-light)',
            }}
          >← {t.cs_back_dashboard || 'Workspace'}</button>
          <h1 className="dashboard-title">{t.moodboards}</h1>
        </div>
        <div className="dashboard-header-actions" style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="boards-settings-link"
            onClick={() => navigate('/boards/settings')}
            style={{
              background: 'transparent', border: 'none', padding: '0 8px 4px', cursor: 'pointer',
              fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: 'var(--kahla-text-light)',
              borderBottom: '1px solid transparent', transition: 'color 200ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-text-light)'; }}
          >{t.boards_settings_title || 'Board Settings'}</button>
          <button
            className="btn-primary dashboard-new-board-btn"
            onClick={() => setShowCreateModal(true)}
            disabled={accessStatus === 'expired_read_only'}
            style={{ marginLeft: '12px', ...(accessStatus === 'expired_read_only' ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
          >
            <span className="dashboard-new-board-btn__full">{t.new_board}</span>
            <span className="dashboard-new-board-btn__compact" aria-hidden="true">+ New</span>
          </button>
        </div>
      </header>

      {/* Storage warning banner — appears only at ≥80% usage */}
      <StorageWarningBanner />

      {/* Onboarding welcome card — shown only on first visit, until dismissed */}
      {onboarding && !onboarding.seen_welcome && (
        <WelcomeCard
          t={t}
          lang={workspaceLang}
          demoBoardId={onboarding.demo_board?.id || null}
          onDismiss={() => setOnboarding({ ...onboarding, seen_welcome: true })}
          onCreate={() => setShowCreateModal(true)}
        />
      )}

      {/* Archive Mode banner — calm taupe band when subscription has expired
          but the 30-day grace window is still open. */}
      <ArchiveModeBanner archive={archiveMode} />

      {/* Admin support view banner */}
      {isSupportView && (
        <div data-testid="support-view-banner" style={{
          padding: '10px 24px',
          background: '#e3f2fd',
          border: '1px solid #bbdefb',
          marginBottom: '24px',
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
        }}>
          <p style={{ fontSize: '12px', color: '#1565c0', fontWeight: '500' }}>
            Admin support view — viewing user workspace
          </p>
          <button
            data-testid="exit-support-view"
            onClick={() => { Cookies.remove('kahla_token'); Cookies.remove('kahla_role'); navigate('/login'); }}
            style={{
              padding: '4px 12px', fontSize: '11px', border: '1px solid #1565c040',
              background: '#fff', color: '#1565c0', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Exit</button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
          <div className="spinner"></div>
        </div>
      ) : boards.length === 0 ? (
        <div className="empty-state">
          <h2 className="empty-state-title" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400, fontSize: '24px' }}>
            {t.no_boards_title}
          </h2>
          <p className="empty-state-text" style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: '15px',
            color: 'var(--kahla-text-light)',
            maxWidth: 480,
            margin: '12px auto 32px',
            lineHeight: 1.7,
          }}>
            {t.empty_no_boards}
          </p>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            {t.create_first_board}
          </button>
        </div>
      ) : (() => {
        // ── Status filter (Aktywne / Wygasłe / Wszystkie) ──────────
        const isExpiredBoard = (b) => ['EXPIRED', 'ARCHIVED'].includes(b.current_status);
        const visibleBoards = boards.filter((b) => {
          if (statusFilter === 'active') return !isExpiredBoard(b);
          if (statusFilter === 'expired') return isExpiredBoard(b);
          return true;
        });

        const filterBar = (
          <nav
            data-testid="board-status-filter"
            aria-label="Board status filter"
            style={{ display: 'flex', alignItems: 'baseline', gap: '26px', marginBottom: '34px' }}
          >
            {[
              { key: 'active', label: t.filter_active || 'Active' },
              { key: 'expired', label: t.filter_expired || 'Expired' },
              { key: 'all', label: t.filter_all || 'All' },
            ].map(({ key, label }) => {
              const selected = statusFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  data-testid={`board-filter-${key}`}
                  aria-pressed={selected}
                  style={{
                    background: 'transparent', border: 'none', padding: '0 0 6px', cursor: 'pointer',
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase',
                    color: selected ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                    borderBottom: selected ? '1px solid var(--kahla-charcoal)' : '1px solid transparent',
                    transition: 'color 200ms ease, border-color 200ms ease',
                  }}
                >{label}</button>
              );
            })}
          </nav>
        );

        if (visibleBoards.length === 0) {
          return (
            <>
              {filterBar}
              <p data-testid="board-filter-empty" style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic', fontSize: '15px',
                color: 'var(--kahla-text-light)', padding: '32px 0 64px',
              }}>
                {t.filter_empty || 'Nothing here — adjust the filter above.'}
              </p>
            </>
          );
        }

        // ── Board Type Split (2026-03) ─────────────────────────────
        // Two distinct sections on the dashboard so the studio can
        // instantly tell active client projects apart from curated
        // concept presentations. We do NOT use tabs — both lists are
        // rendered top-to-bottom with generous breathing room so the
        // editorial rhythm of the page stays intact. Existing card
        // chrome, sorting and badges are untouched.
        const clientBoards = visibleBoards.filter(b => (b.board_type || 'client') === 'client');
        const inspirationBoards = visibleBoards.filter(b => b.board_type === 'inspiration');

        const renderCard = (board) => (
          <div
            key={board.id}
            className="board-card"
            data-testid={`board-card-${board.id}`}
            onClick={() => navigate(`/editor/${board.id}`)}
          >
            <div className="board-card-cover">
              {board.cover_image_url ? (
                <img src={board.cover_image_url} alt={board.name} loading="lazy" />
              ) : (
                <div className="board-card-cover-placeholder">Untitled</div>
              )}
              <span
                className={getStatusBadgeClass(board.current_status)}
                style={getStatusBadgeStyle(board.current_status)}
              >
                {board.current_status === 'OPEN'
                  ? (t.status_open || 'Open')
                  : board.current_status === 'LOCKED'
                    ? (t.status_locked || 'Locked')
                    : (board.current_status === 'EXPIRED' || board.current_status === 'ARCHIVED')
                      ? (t.status_expired || 'Expired')
                      : board.current_status}
              </span>
            </div>
            <div className="board-card-body">
              <div className="board-card-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 className="board-card-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{board.name}</span>
                    {board.has_unread_client_activity && (
                      <span
                        data-testid={`unread-badge-${board.id}`}
                        className="activity-dot"
                        title="New client activity"
                        aria-label="New client activity"
                      >
                        <span className="activity-dot__core" />
                        <span className="activity-dot__pulse" aria-hidden="true" />
                      </span>
                    )}
                  </h3>
                  {board.client_name && <p className="board-card-client">{board.client_name}</p>}
                </div>
              </div>

              <div className="board-card-meta">
                <span>{board.image_count || 0} {t.images}</span>
                <span>·</span>
                <span>{t.expires} {formatDate(board.expires_at)}</span>
              </div>

              <button
                className="btn-ghost"
                style={{ alignSelf: 'flex-start', padding: '8px 0', fontSize: '11px', color: 'var(--kahla-text-light)' }}
                onClick={(e) => handleDuplicate(e, board.id)}
              >
                {t.duplicate}
              </button>
            </div>
          </div>
        );

        // Editorial section header — calm Playfair eyebrow + Cormorant
        // italic subtitle. Both copies are localised; English fallback
        // is inlined so the dashboard never blanks while i18n loads.
        const SectionHeader = ({ title, subtitle, testId }) => (
          <header
            data-testid={testId}
            style={{
              padding: '0 0 18px',
              borderBottom: '1px solid var(--kahla-border)',
              marginBottom: 28,
            }}
          >
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(20px, 2.2vw, 26px)',
              fontWeight: 400,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--kahla-charcoal, var(--kahla-text))',
              margin: 0,
            }}>{title}</h2>
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 'clamp(13px, 1.4vw, 15px)',
              color: 'var(--kahla-text-light)',
              lineHeight: 1.55,
              margin: '6px 0 0',
              maxWidth: 640,
            }}>{subtitle}</p>
          </header>
        );

        // Calm in-section empty state — never a loud CTA, never
        // technical copy. The italic line nudges the studio toward
        // creating the first board of this type.
        const EmptySection = ({ message, testId }) => (
          <div
            data-testid={testId}
            style={{
              padding: '40px 24px',
              border: '1px dashed var(--kahla-border)',
              textAlign: 'center',
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 15,
              color: 'var(--kahla-text-light)',
              lineHeight: 1.6,
            }}
          >
            {message}
          </div>
        );

        return (
          <>
            {filterBar}
            {/* CLIENT BOARDS — active collaboration */}
            <section
              data-testid="dashboard-section-client"
              style={{ marginBottom: 64 }}
            >
              <SectionHeader
                title={t.dash_section_client_title || 'CLIENT BOARDS'}
                subtitle={t.dash_section_client_subtitle || 'Active projects and client collaboration — feedback, decisions, and workflows.'}
                testId="dashboard-section-client-header"
              />
              {clientBoards.length === 0 ? (
                <EmptySection
                  message={t.dash_empty_client || 'Create your first Client Board.'}
                  testId="dashboard-empty-client"
                />
              ) : (
                <div className="boards-grid">
                  {clientBoards.map(renderCard)}
                </div>
              )}
            </section>

            {/* INSPIRATION BOARDS — curated concept presentations */}
            <section data-testid="dashboard-section-inspiration">
              <SectionHeader
                title={t.dash_section_inspiration_title || 'INSPIRATION BOARDS'}
                subtitle={t.dash_section_inspiration_subtitle || 'Concepts, portfolio examples and visual direction.'}
                testId="dashboard-section-inspiration-header"
              />
              {inspirationBoards.length === 0 ? (
                <EmptySection
                  message={t.dash_empty_inspiration || 'Create your first Inspiration Board.'}
                  testId="dashboard-empty-inspiration"
                />
              ) : (
                <div className="boards-grid">
                  {inspirationBoards.map(renderCard)}
                </div>
              )}
            </section>
          </>
        );
      })()}

      {showCreateModal && (
        <CreateBoardModal 
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateBoard}
          lang={workspaceLang}
        />
      )}

      <footer data-testid="dashboard-footer" style={{
        padding: '32px 24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        marginTop: '48px',
      }}>
        <PlatformFooter />
        <LegalFooterLinks marginTop={14} />
      </footer>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export default Dashboard;
